import {announce} from '@github-ui/aria-live'
import {sendEvent} from '@github-ui/hydro-analytics'
import ProductAnnouncement from '@github-ui/product-announcement-shared/components/ProductAnnouncement'
import type {
  ProductAnnouncementBannerEvent,
  ProductAnnouncementModalEvent,
  ProductAnnouncementModalEventPayload,
} from '@github-ui/product-announcement-shared/components/ProductAnnouncement.types'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Banner} from '@primer/react'
import {useEffect, useEffectEvent, useRef, useState, type RefObject} from 'react'

import type {PromotionalBannerProps} from '../promotional-banner/PromotionalBanner'
import {announcementData} from './announcement-data'

interface AnnouncementBannerProps extends PromotionalBannerProps {
  returnFocusRef?: RefObject<HTMLElement | null>
}

export function AnnouncementBanner({banner, returnFocusRef}: AnnouncementBannerProps) {
  const data = announcementData(banner.announcement)
  const [dismissState, setDismissState] = useState<'idle' | 'pending' | 'error' | 'dismissed'>('idle')
  const [hasDismissError, setHasDismissError] = useState(false)
  const [isBannerDisplayed, setIsBannerDisplayed] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const bannerRef = useRef<HTMLDivElement>(null)
  const dismissInFlightRef = useRef(false)
  const restoreFocusAfterDismissRef = useRef(false)
  const dismissContextRef = useRef<'banner' | 'modal'>('banner')
  const bannerReportedRef = useRef(false)
  const modalReportedRef = useRef(false)
  const isRenderable = Boolean(data) && isBannerDisplayed && dismissState !== 'dismissed'

  const report = (action: string, context: 'banner' | 'modal', visible = false) => {
    sendEvent(visible ? 'analytics.visible' : 'analytics.click', {
      category: banner.analytics.category,
      action,
      label: `${banner.analytics.label}location:${banner.analytics.location};context:${context};`,
    })
  }

  const reportBannerVisible = useEffectEvent(() => {
    if (bannerReportedRef.current) return
    bannerReportedRef.current = true
    report('visible', 'banner', true)
  })

  useEffect(() => {
    const element = bannerRef.current
    if (!isRenderable || !element) return
    const observer = new IntersectionObserver(entries => {
      if (
        !entries.some(
          entry => entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0,
        ) ||
        !element.checkVisibility({checkVisibilityCSS: true})
      )
        return
      reportBannerVisible()
      observer.disconnect()
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [isRenderable])

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- focus must follow modal unmount cleanup, not the request callback
    if (dismissState !== 'dismissed' || !restoreFocusAfterDismissRef.current) return
    restoreFocusAfterDismissRef.current = false
    const focusTarget = returnFocusRef?.current ?? containerRef.current
    focusTarget?.focus()
  }, [dismissState, returnFocusRef])

  const dismiss = async (context: 'banner' | 'modal') => {
    if (dismissInFlightRef.current || dismissState === 'dismissed') return
    dismissInFlightRef.current = true
    dismissContextRef.current = context
    setDismissState('pending')
    try {
      const response = await verifiedFetch(banner.dismissPath, {method: 'POST'})
      if (!response.ok) throw new Error('Announcement dismissal failed')
      report('dismiss', context)
      restoreFocusAfterDismissRef.current =
        Boolean(containerRef.current?.contains(document.activeElement)) || modalReportedRef.current
      setDismissState('dismissed')
    } catch {
      setDismissState('error')
      setHasDismissError(true)
      announce("Couldn't dismiss this announcement. Your announcement is still available. Try again.", {
        assertive: true,
      })
    } finally {
      dismissInFlightRef.current = false
    }
  }

  const onBannerEvent = (event: ProductAnnouncementBannerEvent) => {
    if (event === 'displayed') setIsBannerDisplayed(true)
    if (event === 'clicked') report(banner.primaryAction.analyticsAction, 'banner')
  }

  const onModalEvent = (event: ProductAnnouncementModalEvent, payload?: ProductAnnouncementModalEventPayload) => {
    if (event === 'closed') modalReportedRef.current = false
    if (event === 'opened' && !modalReportedRef.current) {
      modalReportedRef.current = true
      report('visible', 'modal', true)
    }
    if ((event === 'tab_cta1_clicked' || event === 'tab_cta2_clicked') && payload?.analyticsAction) {
      report(payload.analyticsAction, 'modal')
    }
  }

  if (!data) return null

  return (
    <div
      ref={containerRef}
      role="group"
      tabIndex={dismissState === 'dismissed' ? -1 : undefined}
      aria-label={dismissState === 'dismissed' ? 'Announcement dismissed' : undefined}
      data-testid={dismissState === 'dismissed' ? undefined : banner.testSelector}
    >
      {dismissState !== 'dismissed' ? (
        <div ref={bannerRef}>
          <ProductAnnouncement
            data={data}
            useLocalStorage={false}
            shouldShowMainCta={false}
            shouldShowTabCtas
            onBannerEvent={onBannerEvent}
            onModalEvent={onModalEvent}
            onBannerDismiss={() => void dismiss('banner')}
            onClose={() => void dismiss('modal')}
          />
        </div>
      ) : null}
      {hasDismissError && dismissState !== 'dismissed' ? (
        <Banner
          variant="critical"
          title="Couldn't dismiss this announcement"
          description="Your announcement is still available. Try again."
          primaryAction={
            <Banner.PrimaryAction
              aria-disabled={dismissState === 'pending' || undefined}
              onClick={() => void dismiss(dismissContextRef.current)}
            >
              Retry dismissal
            </Banner.PrimaryAction>
          }
        />
      ) : null}
    </div>
  )
}
